import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { unionBy } from 'lodash';

import superOrganizationService from '../services/superOrganizationService';

const DEFAULT_OELS_PER_PAGE = 20;

export const getSuperOrganizationInfo = createAsyncThunk('superOrganization/getInfo', async () => {
  const res = await superOrganizationService.getInfo();

  return res;
});

export const getSuperOrganizationTeamsInfo = createAsyncThunk('superOrganization/getTeamsInfo', async ({ id }) => {
  const res = await superOrganizationService.getTeamsInfo(id);

  return res;
});

export const updateAgreedToMsa = createAsyncThunk('superOrganization/updateAgreedToMsa', async ({ id, checked }) => {
  const res = await superOrganizationService.updateAgreedToMsa(id, checked);

  return res;
});

export const listSuperOrganizationExternalLinks = createAsyncThunk(
  'superOrganization/listExternalLinks',
  async ({ searchQuery, skip, limit } = {}) => {
    const oels = await superOrganizationService.listExternalLinks({
      searchQuery,
      skip,
      getFirst: limit || DEFAULT_OELS_PER_PAGE
    });

    return oels;
  }
);

export const updateOrganizationProvisioningStatus = createAsyncThunk(
  'superOrganization/updateOrganizationProvisioningStatus',
  async ({ id, status }) => {
    const res = await superOrganizationService.updateOrgProvisioningStatus(id, { provisioning_status: status });

    return res;
  }
);

const updatedExternalLinks = (updatedExternalLink, state) => {
  if (updatedExternalLink) {
    return state.externalLinks.map((link) => {
      if (link.id === updatedExternalLink.id) {
        return updatedExternalLink;
      }
      return link;
    });
  }
  return state.externalLinks;
};

const INITIAL_STATE = {
  data: null,
  isLoading: false,
  isError: false,
  teams: null,
  totalUsers: 0,
  externalLinks: [],
  totalExternalLinks: 0,
  totalProvisioned: 0,
  loadedAllExternalLinks: false,
  searchQuery: ''
};

const superOrganizationSlice = createSlice({
  name: 'superOrganization',
  initialState: { ...INITIAL_STATE },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getSuperOrganizationInfo.fulfilled, (state, action) => {
        state.data = action.payload;
        state.isLoading = false;
        state.isError = false;
      })
      .addCase(getSuperOrganizationInfo.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
      })
      .addCase(getSuperOrganizationInfo.rejected, (state) => {
        state.isLoading = false;
        state.isError = true;
      })
      .addCase(getSuperOrganizationTeamsInfo.fulfilled, (state, action) => {
        const { total_users, organizations } = action.payload;

        state.totalUsers = total_users;
        state.teams = organizations;
        state.isLoading = false;
        state.isError = false;
      })
      .addCase(getSuperOrganizationTeamsInfo.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
      })
      .addCase(getSuperOrganizationTeamsInfo.rejected, (state) => {
        state.isLoading = false;
        state.isError = true;
      })
      .addCase(listSuperOrganizationExternalLinks.fulfilled, (state, action) => {
        const { organization_external_links, total, total_provisioned } = action.payload;
        const query = action.meta?.arg?.query || '';
        const skip = action.meta?.arg?.skip;

        if (query !== state.searchQuery || !skip) {
          state.externalLinks = [...organization_external_links];
        } else {
          state.externalLinks = unionBy(state.externalLinks, organization_external_links, 'id');
        }
        state.totalExternalLinks = total;
        state.totalProvisioned = total_provisioned;
        state.loadedAllExternalLinks = [...state.externalLinks, ...organization_external_links].length === total;
        state.isLoading = false;
        state.isError = false;
        state.searchQuery = query;
      })
      .addCase(listSuperOrganizationExternalLinks.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
      })
      .addCase(listSuperOrganizationExternalLinks.rejected, (state) => {
        state.isLoading = false;
        state.isError = true;
      })
      .addCase(updateOrganizationProvisioningStatus.fulfilled, (state, action) => {
        const updatedExternalLink = action.payload;

        state.externalLinks = updatedExternalLinks(updatedExternalLink, state);
        state.isLoading = false;
        state.isError = false;
      })
      .addCase(updateOrganizationProvisioningStatus.pending, (state) => {
        state.isLoading = true;
        state.isError = false;
      })
      .addCase(updateOrganizationProvisioningStatus.rejected, (state) => {
        state.isLoading = false;
        state.isError = true;
      })
      .addCase('signOutGlobally', (state) => ({ ...INITIAL_STATE }));
  }
});

export default superOrganizationSlice.reducer;
